/**
 * Keyboard triage over a list of rows (Reviews, Email drafts).
 *
 * j / k (or ↓ / ↑ while a row has focus) move a cursor; Enter (native, on the
 * row's link) or `o` opens; `a` approves and `s` snoozes the row under the
 * cursor when the page allows it; `/` focuses the search field; `?` opens
 * the shortcut help. When the row under the cursor leaves the list (approved,
 * snoozed, filtered out) the cursor stays at the same position, so the next
 * row is ready without another keypress.
 *
 * Rows use a roving tabindex on their primary link: spread `rowProps(item)`
 * onto it and put `.row-cursor` on the row when `isActive(item)`. Rows hold
 * buttons of their own, so this is a plain list, not a listbox.
 */
import { useCallback, useRef, useState, type KeyboardEvent, type RefObject } from "react";

import { useKeyboardShortcuts } from "./useKeyboardShortcuts";

interface Options<T> {
  items: readonly T[];
  getKey: (item: T) => string;
  onOpen?: (item: T) => void;
  onApprove?: (item: T) => void;
  canApprove?: (item: T) => boolean;
  onSnooze?: (item: T) => void;
  canSnooze?: (item: T) => boolean;
  searchRef?: RefObject<HTMLInputElement | null>;
  enabled?: boolean;
}

export interface ShortcutHint {
  keys: string[];
  label: string;
}

/** Spread onto a row's primary link (roving tabindex + cursor wiring). */
export interface ListRowProps {
  ref: (el: HTMLElement | null) => void;
  tabIndex: 0 | -1;
  "aria-current": true | undefined;
  "data-row-key": string;
  onFocus: () => void;
  onKeyDown: (e: KeyboardEvent<HTMLElement>) => void;
}

export function useListNavigation<T>({
  items,
  getKey,
  onOpen,
  onApprove,
  canApprove = () => true,
  onSnooze,
  canSnooze = () => true,
  searchRef,
  enabled = true,
}: Options<T>) {
  const [cursor, setCursor] = useState<{ key: string | null; index: number }>({ key: null, index: -1 });
  const [helpOpen, setHelpOpen] = useState(false);
  const elements = useRef(new Map<string, HTMLElement>());

  // The active row: the one under the cursor, or — when it left the list —
  // whatever now sits at the cursor's position.
  const keys = items.map(getKey);
  let activeIndex = cursor.key ? keys.indexOf(cursor.key) : -1;
  if (activeIndex < 0 && cursor.index >= 0 && keys.length > 0) {
    activeIndex = Math.min(cursor.index, keys.length - 1);
  }
  const activeKey = activeIndex >= 0 ? keys[activeIndex] : null;
  const activeItem = activeIndex >= 0 ? items[activeIndex] : undefined;

  const moveTo = useCallback(
    (index: number) => {
      if (keys.length === 0) return;
      const i = Math.max(0, Math.min(index, keys.length - 1));
      setCursor({ key: keys[i], index: i });
      const el = elements.current.get(keys[i]);
      if (el) {
        el.focus({ preventScroll: true });
        el.scrollIntoView({ block: "nearest" });
      }
    },
    [keys],
  );

  const step = (delta: number) => moveTo(activeIndex < 0 ? 0 : activeIndex + delta);

  useKeyboardShortcuts(
    {
      j: () => step(1),
      k: () => step(-1),
      o: () => (activeItem !== undefined ? onOpen?.(activeItem) : undefined),
      a: (e) => {
        if (e.repeat || activeItem === undefined || !onApprove || !canApprove(activeItem)) return;
        onApprove(activeItem);
      },
      s: (e) => {
        if (e.repeat || activeItem === undefined || !onSnooze || !canSnooze(activeItem)) return;
        onSnooze(activeItem);
      },
      "/": () => searchRef?.current?.focus(),
      "?": () => setHelpOpen((v) => !v),
    },
    { enabled },
  );

  const rowProps = (item: T): ListRowProps => {
    const key = getKey(item);
    const active = key === activeKey;
    return {
      ref: (el: HTMLElement | null) => {
        if (el) elements.current.set(key, el);
        else elements.current.delete(key);
      },
      // Roving tabindex: one row in the tab order — the active one, or the
      // first row before any cursor exists.
      tabIndex: (active || (activeKey === null && keys[0] === key) ? 0 : -1) as 0 | -1,
      "aria-current": active ? (true as const) : undefined,
      "data-row-key": key,
      onFocus: () => setCursor({ key, index: keys.indexOf(key) }),
      onKeyDown: (e: KeyboardEvent<HTMLElement>) => {
        if (e.key === "ArrowDown" || e.key === "ArrowUp") {
          e.preventDefault();
          step(e.key === "ArrowDown" ? 1 : -1);
        }
      },
    };
  };

  const shortcuts: ShortcutHint[] = [
    { keys: ["j", "↓"], label: "Next" },
    { keys: ["k", "↑"], label: "Previous" },
    { keys: ["Enter", "o"], label: "Open" },
    ...(onSnooze ? [{ keys: ["s"], label: "Snooze" }] : []),
    ...(onApprove ? [{ keys: ["a"], label: "Approve" }] : []),
    ...(searchRef ? [{ keys: ["/"], label: "Search" }] : []),
    { keys: ["?"], label: "Show shortcuts" },
  ];

  return {
    activeKey,
    isActive: (item: T) => getKey(item) === activeKey,
    rowProps,
    helpOpen,
    setHelpOpen,
    shortcuts,
  };
}
